import React, { useState, useEffect } from 'react';
import { IonIcon, useIonToast } from '@ionic/react';
import { close } from 'ionicons/icons';
import { useUpdateMember } from '@/hooks/useMembers';
import { PASTORAL_CASE_LABEL } from '@/utils/labels';
import type { Member } from '@/types/member';

interface OutreachEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member | null;
}

/**
 * Edits a single entry on the pastoral follow-up list.
 *
 * Two levers only, because only two things here are decisions rather than
 * facts: whether the person is a prodigal, and whether the church has stopped
 * actively pursuing them. "Adult nebotezat" is derived from their baptism and
 * birth date, so it is shown read-only — the way to change it is to record the
 * baptism on the member's own page.
 *
 * Taking someone off the list does NOT return them to the main registry.
 */
const OutreachEntryModal: React.FC<OutreachEntryModalProps> = ({ isOpen, onClose, member }) => {
  const [presentToast] = useIonToast();
  const updateMut = useUpdateMember();

  const [isProdigal, setIsProdigal] = useState(false);
  const [notes, setNotes] = useState('');
  const [removed, setRemoved] = useState(false);
  const [removalReason, setRemovalReason] = useState('');

  useEffect(() => {
    if (isOpen && member) {
      setIsProdigal(!!member.is_prodigal);
      setNotes(member.prodigal_notes ?? '');
      setRemoved(!!member.outreach_excluded_at);
      setRemovalReason(member.outreach_exclusion_reason ?? '');
    }
  }, [isOpen, member]);

  if (!isOpen || !member) return null;

  const handleSave = async () => {
    try {
      await updateMut.mutateAsync({
        id: member.id,
        data: {
          is_prodigal: isProdigal,
          prodigal_notes: notes.trim() || null,
          // Preserve the original timestamp when it was already set, so
          // re-saving a note does not reset "since when".
          outreach_excluded_at: removed
            ? (member.outreach_excluded_at ?? new Date().toISOString())
            : null,
          outreach_exclusion_reason: removed ? (removalReason.trim() || null) : null,
        },
      });
      presentToast({ message: 'Salvat.', duration: 1500, color: 'success' });
      onClose();
    } catch {
      presentToast({ message: 'Nu s-a putut salva.', duration: 2500, color: 'danger' });
    }
  };

  return (
    <div className="logout-overlay" onClick={onClose}>
      <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: '90%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {member.last_name} {member.first_name}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
          </button>
        </div>

        {member.is_unbaptized_adult && (
          <div
            style={{
              marginBottom: 16, padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
              backgroundColor: '#f1f5f9', color: '#475569',
            }}
          >
            Trecut automat la <strong>{PASTORAL_CASE_LABEL}</strong> — a împlinit 18 ani fără botez
            înregistrat. Iese de pe listă de la sine în momentul în care botezul e trecut în fișa persoanei.
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, cursor: 'pointer' }}>
          <input type="checkbox" checked={isProdigal} onChange={(e) => setIsProdigal(e.target.checked)} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>{PASTORAL_CASE_LABEL} (bifat manual)</span>
        </label>
        <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 16px 24px' }}>
          Pentru cineva botezat care s-a răcit. Cei nebotezați de 18+ ajung pe listă fără bifa asta.
        </p>

        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label">Note pastorale</label>
          <textarea
            className="form-input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ce s-a discutat, cine ține legătura..."
          />
        </div>

        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={removed} onChange={(e) => setRemoved(e.target.checked)} />
            <span style={{ fontSize: 14, fontWeight: 600 }}>Scoate din listă</span>
          </label>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0 24px' }}>
            Nu șterge persoana și nu o readuce în registrul principal — doar o scoate din lista de contactat.
          </p>
          {removed && (
            <div className="form-field" style={{ marginTop: 12 }}>
              <label className="form-field__label">Motiv</label>
              <input
                className="form-input"
                value={removalReason}
                onChange={(e) => setRemovalReason(e.target.value)}
                placeholder="Ex: a plecat în străinătate"
              />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="form-btn form-btn--secondary" onClick={onClose}>Anulează</button>
          <button className="form-btn form-btn--primary" onClick={handleSave} disabled={updateMut.isPending}>
            {updateMut.isPending ? 'Se salvează...' : 'Salvează'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OutreachEntryModal;
